import {
  compact,
  every,
  filter,
  flattenDeep,
  get,
  groupBy,
  map,
  orderBy,
  pick,
  pickBy,
  some
} from 'lodash';
import {
  applicationTypes,
  fertilityScriptTypes,
  scriptTypes
} from 'constants/scripts';
import { fertilityProductSubstanceSets } from 'constants/products';

import {
  getAvgNitrogenRateForZones,
  getAvgProductRateForZones,
  getTotalAreaForZones,
  getTotalProductForZones,
  groupZonesByProduct
} from 'util/zoneOperations';

// Selectors
import { getFertilityActivityCategoriesOrdered } from 'activities/selectors';
import { getActiveFieldId, getActiveProductYear } from 'current/selectors';
import { getSubstanceTypesForApplicationType } from 'products/fertility-selectors';
import {
  getScriptsForFieldId,
  getScriptsSortedForManageScriptList,
  getScriptTypeForScript,
  isCompleteScript,
  isPrimaryScript,
  scriptIsInProductYear,
  getApplicationTypeForScriptType
} from 'scripts/selectors';
import {
  getFertilizerApplicationsForZone,
  hasStabilizerApplicationsForZones
} from 'scripts/zones/fertility-selectors';
import { getApplicationsForZoneByType } from './zones/selectors';

export const isFertilityScript = (script) =>
  getScriptTypeForScript(script) === scriptTypes.FERTILITY;

export const hasMissingFertilizerProducts = (script) => {
  const { zones } = script;
  const everyZoneHasFertilizerProduct = every(zones, (zone) => {
    const fertilizerApplication = getFertilizerApplicationsForZone(zone)[0];
    return fertilizerApplication && fertilizerApplication.product;
  });
  const everyZoneHasNoFertilizerProduct = every(zones, (zone) => {
    const fertilizerApplication = getFertilizerApplicationsForZone(zone)[0];
    return !fertilizerApplication || !fertilizerApplication.product;
  });
  return !everyZoneHasFertilizerProduct && !everyZoneHasNoFertilizerProduct;
};

export const hasMissingFertilizerRates = (script) => {
  const { zones } = script;
  const applicationType = getApplicationTypeForScriptType(
    getScriptTypeForScript(script)
  );
  return some(zones, (zone) => {
    const fertilizerApplication = getApplicationsForZoneByType({
      zone,
      applicationType
    })[0];
    const rate = get(fertilizerApplication, 'rate.q');
    return !Number.isFinite(rate);
  });
};

export const getFertilityScriptsForFieldId = (state, fieldId) => {
  const fieldScripts = getScriptsForFieldId(state, fieldId);
  return pickBy(
    fieldScripts,
    (script) => isFertilityScript(script) && isCompleteScript(script)
  );
};

export const getFertilityScriptsSortedForManageScriptList = (
  state,
  fieldScripts
) => {
  // group scripts by activity type
  const fieldScriptsByActivityType = groupBy(
    fieldScripts,
    (script) => script['activity-type']
  );
  const fertilityActivityCategoriesOrdered
    = getFertilityActivityCategoriesOrdered(state);

  // map through fertility categories in order, taking the group of scripts for that activity type
  // and running them through the sorting util for the manage script list. then flatten the whole thing
  // into one array
  return flattenDeep(
    fertilityActivityCategoriesOrdered.map(({ activityType }) => {
      const activityTypeScripts = fieldScriptsByActivityType[activityType];
      return getScriptsSortedForManageScriptList(state, activityTypeScripts);
    })
  );
};

export const getFertilityScriptsForManageScriptList = (state) => {
  // get all scripts for the active field.
  const fieldId = getActiveFieldId(state);
  let fieldScripts = getFertilityScriptsForFieldId(state, fieldId);
  // get only scripts for active product year
  fieldScripts = Object.values(fieldScripts).filter((script) => {
    return scriptIsInProductYear(script, getActiveProductYear(state));
  });

  return getFertilityScriptsSortedForManageScriptList(state, fieldScripts);
};

export const fertilityScriptTypeIsNitrogen = (script) => {
  return get(script, 'tag') === fertilityScriptTypes.NITROGEN;
};

export const getFertilityProductSubstanceSetForScript = (script) => {
  return fertilityScriptTypeIsNitrogen(script)
    ? fertilityProductSubstanceSets.NITROGEN
    : fertilityProductSubstanceSets.LPK;
};

export const getManageFertilizerSummary = (script) => {
  const { zones } = script;
  const applicationType = getApplicationTypeForScriptType(
    getScriptTypeForScript(script)
  );
  const substanceSet = getFertilityProductSubstanceSetForScript(script);
  const zonesByProduct = groupZonesByProduct({ zones, applicationType });
  return orderBy(
    compact(
      map(zonesByProduct, (productZones, productName) => {
        const sampleZone = productZones[0];
        const sampleApplication = getApplicationsForZoneByType({
          zone: sampleZone,
          applicationType
        })[0];
        const product = get(sampleApplication, 'product');
        if (!product) {
          return null;
        }

        const values = {
          area: getTotalAreaForZones(productZones),
          avgNitrogenRate: getAvgNitrogenRateForZones(productZones),
          avgRate: getAvgProductRateForZones(productZones, applicationType),
          product,
          productName,
          substanceSet,
          totalProduct: getTotalProductForZones({
            zones: productZones,
            applicationType
          })
        };
        return values;
      })
    ),
    ['productName']
  );
};

export const getEditFertilizerSummary = (script) => {
  const fertilizerSummary = getManageFertilizerSummary(script)[0];
  return pick(fertilizerSummary, ['avgNitrogenRate', 'avgRate', 'totalProduct']);
};

export const getActivityCategoryUuidForScript = (script) =>
  script['activity-uuid'];

export const getPrimaryFertilityScriptsForFieldId = (state, fieldId) => {
  const scripts = getFertilityScriptsForFieldId(state, fieldId);
  return filter(scripts, isPrimaryScript);
};

export const hasNitrapyrinForScript = (script) => {
  const { zones } = script;
  return hasStabilizerApplicationsForZones(zones);
};

export const getFertilityScriptTypeBySubstanceTypeUuid = (
  state,
  substanceTypeUuid
) => {
  const { byUuid } = getSubstanceTypesForApplicationType(
    state,
    applicationTypes.FERTILIZER
  );
  const substanceTypeName = byUuid[substanceTypeUuid];
  return fertilityScriptTypes[substanceTypeName.toUpperCase()];
};

export const getSubstanceTypeUuidForFertilityScriptType = (
  state,
  fertilityScriptType
) => {
  const substanceTypeName = fertilityScriptType.substr(
    fertilityScriptType.lastIndexOf('-') + 1
  );
  const { byName } = getSubstanceTypesForApplicationType(
    state,
    applicationTypes.FERTILIZER
  );
  return byName[substanceTypeName];
};
